import * as $ from "jquery";
import axios from 'axios';
import Storage from '../../customer-experience/modules/atl-storage';
import Environment from '../../common/environment';
import { Segment } from '../../common/tracking/platforms';

export default class LanguageSwitcherNav {
  componentRoot: JQuery<HTMLElement>;
  params: any;
  storage: any;
  currentLocale: string;
  preferredLocale: null;
  bannerDismissStorageKey: string;
  preferredLocaleStorageKey: string;
  activeBannerClass: string;
  dissmissButton: any;
  selectTrigger: any;
  preferredLocaleLink: any;
  preferredLocaleLabel: any;
  
  constructor(componentRoot: HTMLElement, params: any) {
    this.componentRoot = $(componentRoot);
    this.params = { ...params };

    this.storage = new Storage();

    this.currentLocale = this.getCurrentLocale();
    this.preferredLocale = null;

    this.bannerDismissStorageKey = 'language-selector-banner-dismissed';
    this.preferredLocaleStorageKey = 'language-selector-preferred';
    this.activeBannerClass = 'language-selector-banner--open';

    this.dissmissButton = this.componentRoot.find('.language-selector-banner__close-banner');
    this.selectTrigger = this.componentRoot.find('.language-selector-banner__language-selector__trigger');
    this.preferredLocaleLink = this.componentRoot.find('.language-selector-banner__language-suggestion > a');
    this.preferredLocaleLabel = this.preferredLocaleLink.find('.preferred-locale-detected');

    this.init();
  }

  init () {
    this.fixLanguageBannerForCloudSignup();
    this.preselectFooterLanguage(this.currentLocale);

    if (!this.languageSelectorDismissed()) {
      this.checkLocales();
    }
  }

  checkLocales() {
    return this.detectBrowserLocale().then(locale => {
      this.storage.session.set(this.preferredLocaleStorageKey, locale);
      this.preferredLocale = locale;

      if (this.isLocaleDifferent()) {
        this.bindEvents();
        this.preselectBannerLanguage(locale);
      }
    })
    .catch(() => {
      Segment.track({
        category: 'bxp',
        action: 'language-selector.api.error',
        label: 'language-selector',
      });
    });

  }

  /**
   * This is required for WPI-1600, since gateway removes all query params
   * before proxying to Magnolia. Hence in the language switcher, Magnolia loses
   * context of which signup landing page it was in. This fix will add the browser
   * query params to Language switcher
   */
  fixLanguageBannerForCloudSignup() {
      if (window.location.pathname.includes("/try/cloud/signup") || 
          window.location.pathname.includes("/signup/rename/site") || 
          window.location.pathname.includes("/ondemand/signup/confirmation")) {
          this.componentRoot.find('.language-selector-banner__language-selector__options__list__item a').each(function() {
              $(this).attr("data-locale-url", $(this).attr("data-locale-url") + window.location.search);
              $(this).attr("href", $(this).attr("href") + window.location.search);
          });
      }

  }

  preselectFooterLanguage(locale) {
    const footer = $('#footer');
    const footerLanguageLabel = footer.find('.language-selector--text');
    const selectableOption = footer.find(`.language-selector option[data-lang="${locale}"]`);

    if (selectableOption.length) {
      selectableOption.prop('selected', true);
      footerLanguageLabel.html(selectableOption.text());
    }
  }

  preselectBannerLanguage(preferredLocale) {
    const availableLanguagesCurrentLanguage = this.componentRoot.find(`[data-lang="${this.currentLocale}"]`);
    const availableLanguagesPreferredLanguage = this.componentRoot.find(`[data-lang="${preferredLocale}"]`);

    if (availableLanguagesCurrentLanguage.length) {
      availableLanguagesCurrentLanguage.parent().addClass('language-selector-banner__language-selector__options__list__item--selected');
    }

    if (availableLanguagesPreferredLanguage.length) {
      const preferredLocaleHref = availableLanguagesPreferredLanguage.data('locale-url');
      const preferredLocaleLabel = availableLanguagesPreferredLanguage.text();

      this.preferredLocaleLink.attr('href', preferredLocaleHref);
      this.preferredLocaleLabel.text(preferredLocaleLabel);

      this.showBanner();
    } else {
      Segment.track({
        category: 'bxp',
        action: `language-selector.preferredLocaleNotAvailable_${preferredLocale}`,
        label: 'language-selector',
      });
    }

  }

  languageSelectorDismissed() {
    return this.storage.local.get(this.bannerDismissStorageKey);
  }

  detectBrowserLocale() {
    const preferredLocale = this.getPreferredLocale();
    if (this.getPreferredLocale()) {
      return Promise.resolve(preferredLocale);
    }

    return axios.get(`${Environment.getWacIntegrationUrl()}/endpoint/info/lang`)
      .then(response => {
        return response.data;
      });
  }

  getCurrentLocale() {
    const i18nLocale = Environment.getInternationalSubdomain() || Environment.getInternationalSubdirectory();

    return i18nLocale ? i18nLocale : 'en';
  }

  getPreferredLocale() {
    const preferredLocale = this.storage.session.get(this.preferredLocaleStorageKey);
    if (preferredLocale) {
      return Promise.resolve(preferredLocale);
    }
    return null;
  }

  isLocaleDifferent() {
    return this.currentLocale !== this.preferredLocale;
  }

  bindEvents() {
    this.selectTrigger.on('click', (event) => {
      event.stopPropagation();
      this.toggleSelect();
    });

    this.dissmissButton.on('click', () => {
      this.dismissBanner();
    });

    $(document).on('click', () => {
      this.hideSelect();
    })
  }

  hideSelect() {
    this.componentRoot.removeClass(this.activeBannerClass);
  }

  toggleSelect() {
    this.componentRoot.toggleClass(this.activeBannerClass);
  }

  showBanner() {
    this.componentRoot.show();
    document.body.classList.add('has-lang-select-banner');
    Segment.track({
      category: 'bxp',
      action: `language-selector.shown.preferredLocale_${this.preferredLocale}.currentLocale_${this.currentLocale}`,
      label: 'language-selector',
    });
  }


  dismissBanner() {
    this.storage.local.set(this.bannerDismissStorageKey, 1);
    this.componentRoot.hide();
    document.body.classList.remove('has-lang-select-banner');
    Segment.track({
      category: 'bxp',
      action: `language-selector.dismissed.preferredLocale_${this.preferredLocale}.currentLocale_${this.currentLocale}`,
      label: 'language-selector',
    });
  }
}
